import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect} from 'react';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ApplicationCredentialsModel} from '../../../models/api/applicationCredentialsModel';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {ServerModel, UnsavedServerModel} from '../../../models/api/serverModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
  SettingsServersUrl,
} from '../../../models/url/settingsUrlModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {
  useFetchApplicationCredentials,
  useInvalidateApplicationCredentials,
} from '../../fetchers/applicationCredentials';
import {useFetchServers} from '../../fetchers/servers';
import {ServerMutationsEnum} from '../../fetchers/serversMutations';
import {useSearchQuery} from '../hooks/useSearchQuery';
import {ServerEditorSkeleton} from './serverEditorSkeleton';
import {ServerEditorWrapper} from './serverEditorWrapper';
import {useServersInfoBox} from './serversInfoBox';
import {ServersList} from './serversList';

/*
 * Props.
 */

interface SettingsServersProps {
  panelId: SettingsPanelId;
  url: SettingsServersUrl;
  application: LocalApplicationModel | undefined;
  installedApplication: ApplicationModel;
  setPageTitle: (title: React.ReactNode) => void;
  setPageActions: (title: React.ReactNode) => void;
}

/*
 * Component.
 */

export const SettingsServers: FC<SettingsServersProps> = (props) => {
  const {panelId, url, application, installedApplication, setPageTitle, setPageActions} = props;
  const {resourcePage, applicationId, serverId} = url;

  const hasLocalApplication = Boolean(application);

  const {searchQuery, resetSearchQuery, onSearchQueryChange} = useSearchQuery(url);

  const company = useAppSelector(getCompany);
  const {
    items: servers,
    loadMore,
    moreStatus,
    hasError,
    mutate,
  } = useFetchServers(installedApplication.localApplicationUrl ?? installedApplication.url, searchQuery);

  const server = serverId ? servers.find(({id}) => id === serverId) : undefined;

  const {applicationCredentials, status: credentialsStatus} = useFetchApplicationCredentials(server);
  const invalidateApplicationCredentials = useInvalidateApplicationCredentials();

  const {infoBox, infoBoxIcon} = useServersInfoBox();

  const {navigateToLink} = useLinkNavigation();
  const navigateTo = useCallback(
    (page: SettingsResourcePagesEnum, id?: number | string) =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.SERVERS,
          resourcePage: determineNewResourcePage(hasLocalApplication, page, id),
          applicationId,
          serverId: id,
          searchQuery,
        }),
      ),
    [hasLocalApplication, applicationId, navigateToLink, searchQuery],
  );

  useEffect(() => {
    setPageTitle(renderPageTitle(url, installedApplication, server));
    setPageActions(resourcePage === SettingsResourcePagesEnum.OVERVIEW && !url.serverId ? infoBoxIcon : null);
  }, [installedApplication, infoBoxIcon, resourcePage, server, setPageActions, setPageTitle, url]);

  const onSaveServer = (
    unsavedServer: UnsavedServerModel,
    unsavedCredentials: ApplicationCredentialsModel,
  ) => {
    invalidateApplicationCredentials(server);

    // Without a local application, only save credentials
    if (!application) {
      // This installation must have a server.
      if (!server) {
        return undefined;
      }

      return mutate({
        type: ServerMutationsEnum.SAVE_CREDENTIALS,
        payload: {
          server,
          credentials: unsavedCredentials,
        },
      });
    }

    if (server) {
      // This local application server must be writable.
      if (!server.isWritable) {
        return undefined;
      }

      return mutate({
        type: ServerMutationsEnum.EDIT,
        payload: {
          id: server.id,
          url: server.url,
          server: unsavedServer,
          credentials: unsavedCredentials,
        },
      });
    }

    return mutate({
      type: ServerMutationsEnum.CREATE,
      payload: {
        application,
        server: unsavedServer,
        credentials: unsavedCredentials,
      },
    });
  };

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    if (url.serverId) {
      if (credentialsStatus !== AsyncStatusesEnum.LOADED) {
        return <ServerEditorSkeleton server={server} />;
      }
      return (
        <ServerEditorWrapper
          panelId={panelId}
          installedApplication={installedApplication}
          server={server}
          applicationCredentials={applicationCredentials}
          isReadOnly
          onSaveServer={onSaveServer}
          navigateTo={navigateTo}
        />
      );
    }

    return (
      <ServersList
        servers={servers}
        onLoadMore={loadMore}
        moreStatus={moreStatus}
        hasError={hasError}
        hasLocalApplication={hasLocalApplication}
        onDeleteServers={(serverIds) =>
          mutate({
            type: ServerMutationsEnum.DELETE,
            payload: {
              company,
              serverIds,
            },
          })
        }
        infoBox={infoBox}
        searchQuery={searchQuery}
        resetSearchQuery={resetSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        navigateTo={navigateTo}
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW || resourcePage === SettingsResourcePagesEnum.EDIT) {
    if (credentialsStatus !== AsyncStatusesEnum.LOADED) {
      return <ServerEditorSkeleton server={server} />;
    }

    return (
      <ServerEditorWrapper
        panelId={panelId}
        installedApplication={installedApplication}
        server={server}
        applicationCredentials={applicationCredentials}
        onSaveServer={onSaveServer}
        navigateTo={navigateTo}
      />
    );
  }

  return null;
};

/*
 * Helpers.
 */

function renderPageTitle(
  url: SettingsServersUrl,
  installedApplication?: ApplicationModel,
  server?: ServerModel,
) {
  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    return <Trans id="servers-new-title">Create server</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return <Trans id="servers-edit-title">Edit server for {server?.origin}</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && url.serverId) {
    return <Trans id="servers-details-title">Server for {server?.origin}</Trans>;
  }
  return installedApplication?.name;
}

const determineNewResourcePage = (
  hasLocalApplication: boolean,
  page: SettingsResourcePagesEnum,
  // ID may be a number (local application server ID) or a string (installed application server origin)
  id?: number | string,
) => {
  if (page === SettingsResourcePagesEnum.NEW) {
    return SettingsResourcePagesEnum.NEW;
  }

  if (hasLocalApplication && id) {
    return SettingsResourcePagesEnum.EDIT;
  }

  return SettingsResourcePagesEnum.OVERVIEW;
};
